<script lang="ts">
  import { onMount } from 'svelte';
  import {
    deleteCountdown,
    listCountdowns,
    sortSoonestToFurthest,
    type Countdown,
  } from '../lib/storage';
  import CountdownCard from '../components/CountdownCard.svelte';
  import { href } from '../lib/router';

  let countdowns: Countdown[] = $state([]);
  let loaded = $state(false);
  let isIOS = $state(false);
  let isStandalone = $state(false);
  /// "Get the native app" promo banner — only shown on Apple devices where
  /// the native iOS/Catalyst app could actually run, never inside an
  /// already-installed PWA, and not after the user has dismissed it.
  let appleBannerVisible = $state(false);
  /// iOS-style edit mode: header swaps Edit ↔ Done, cards become non-tappable
  /// and reveal a red minus button that triggers a delete-with-confirm. Auto
  /// exits when the list runs out of rows so we never sit in an unusable state.
  let editing = $state(false);
  /// Ticking clock that drives a live re-sort. A countdown set for today at
  /// 23:30 sits in the "future" bucket all day; once midnight rolls past it
  /// flips into the "past" tail. Without this tick the list froze the sort
  /// at mount time and the user only saw the new order after a reload.
  let now = $state(new Date());

  onMount(() => {
    void (async () => {
      countdowns = await listCountdowns();
      loaded = true;
    })();

    const ua = navigator.userAgent;
    isIOS = /iPhone|iPad|iPod/i.test(ua);
    isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;

    // Includes iPadOS pretending to be Mac (Macintosh UA + touch points).
    const isApple = isIOS || /Macintosh/.test(ua);
    const dismissed = localStorage.getItem('appleBannerDismissed') === '1';
    if (isApple && !isStandalone && !dismissed) {
      appleBannerVisible = true;
    }

    // 30s tick is plenty — the only thing that can change order is the
    // midnight crossing, and we don't need sub-second precision for that.
    const tick = setInterval(() => { now = new Date(); }, 30_000);
    return () => clearInterval(tick);
  });

  const sortedCountdowns = $derived(sortSoonestToFurthest(countdowns, now));

  function dismissAppleBanner() {
    appleBannerVisible = false;
    try { localStorage.setItem('appleBannerDismissed', '1'); } catch {}
  }

  async function handleDelete(c: Countdown) {
    if (!confirm(`Delete "${c.title}"?`)) return;
    await deleteCountdown(c.id);
    countdowns = countdowns.filter((x) => x.id !== c.id);
    if (countdowns.length === 0) editing = false;
  }
</script>

<main>
  <header>
    <div class="title-group">
      <h1>Countdown</h1>
      {#if loaded && countdowns.length > 0}
        <button
          type="button"
          class="edit-toggle"
          onclick={() => (editing = !editing)}
        >
          {editing ? 'Done' : 'Edit'}
        </button>
      {/if}
    </div>
    <a class="new" href={href('/new')} aria-label="New countdown">
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" fill="none" />
      </svg>
    </a>
  </header>

  {#if appleBannerVisible}
    <aside class="apple-banner">
      <img class="banner-icon" src="/apple-touch-icon.png" alt="" />
      <div class="banner-text">
        <div class="banner-title">Get the native app</div>
        <div class="banner-subtitle">Widgets, notifications and more.</div>
      </div>
      <a class="banner-cta" href="https://apps.apple.com/app/id1403367428" target="_blank" rel="noopener">Get</a>
      <button class="banner-dismiss" type="button" onclick={dismissAppleBanner} aria-label="Dismiss">×</button>
    </aside>
  {/if}

  {#if !loaded}
    <!-- IndexedDB load usually completes in <50ms; intentional silence avoids flicker -->
  {:else if countdowns.length === 0}
    <section class="empty">
      <p class="tagline">Beautiful countdowns for the moments that matter.</p>
      <a class="cta" href={href('/new')}>Create your first countdown</a>
      <p class="alt">
        Also on
        <a class="link" href="https://apps.apple.com/app/id1403367428" target="_blank" rel="noopener"
          >the App Store</a
        >.
      </p>
      {#if isIOS && !isStandalone}
        <p class="install-hint">
          To install this web app: tap <strong>Share</strong> → <strong>Add to Home Screen</strong>.
        </p>
      {/if}
    </section>
  {:else}
    <section class="list">
      {#each sortedCountdowns as c (c.id)}
        <CountdownCard
          countdown={c}
          editing={editing}
          ondelete={() => handleDelete(c)}
        />
      {/each}
    </section>
  {/if}
</main>

<style>
  main {
    max-width: 720px;
    margin: 0 auto;
    padding:
      max(24px, env(safe-area-inset-top))
      max(20px, env(safe-area-inset-right))
      max(40px, env(safe-area-inset-bottom))
      max(20px, env(safe-area-inset-left));
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }
  h1 {
    font-size: 1.75rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0;
  }
  .title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  /* Translucent-blur pill in the same family as /to's back/save buttons.
     Uses --ios-segment-bg (iOS systemFillTertiary), which already adapts
     light/dark, so the pill reads as a soft chip on both modes. Sits next
     to the title so it acts on "this list", not as a peer of the + button. */
  .edit-toggle {
    appearance: none;
    border: 0;
    background: var(--ios-segment-bg);
    color: inherit;
    padding: 6px 14px;
    border-radius: 999px;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    transition: background 0.15s ease, transform 0.1s ease;
  }
  .edit-toggle:hover { background: var(--ios-segment-selected); }
  .edit-toggle:active { transform: scale(0.96); }
  .new {
    appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--ios-tint);
    color: #fff;
    text-decoration: none;
  }
  .new svg {
    width: 14px;
    height: 14px;
    display: block;
  }

  .empty {
    text-align: center;
    padding: 8vh 0 40px;
  }
  .tagline {
    font-size: 1.05rem;
    line-height: 1.5;
    opacity: 0.7;
    margin: 0 0 1.5rem;
    max-width: 26rem;
    margin-inline: auto;
  }
  .cta {
    display: inline-block;
    padding: 0.875rem 1.5rem;
    border-radius: 14px;
    background: var(--ios-tint);
    color: #fff;
    font-weight: 600;
    text-decoration: none;
  }
  .alt {
    margin: 1.5rem 0 0;
    font-size: 0.9rem;
    opacity: 0.6;
  }
  .link {
    color: inherit;
    text-decoration: underline;
  }
  .install-hint {
    font-size: 0.85rem;
    opacity: 0.55;
    margin: 1.25rem 0 0;
  }

  .list {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  /* Apple-platform promo banner — sits between the title row and the list.
     Card-like styling (matches the iOS form card aesthetic) with an iOS-blue
     CTA pill on the right and a discreet dismiss button. */
  .apple-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--ios-card-bg);
    border-radius: 14px;
    padding: 10px 12px;
    margin: 0 0 18px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  }
  .banner-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    flex-shrink: 0;
  }
  .banner-text {
    flex: 1;
    min-width: 0;
  }
  .banner-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--ios-label);
  }
  .banner-subtitle {
    font-size: 0.78rem;
    color: var(--ios-label-secondary);
    margin-top: 2px;
    line-height: 1.3;
  }
  .banner-cta {
    background: var(--ios-tint);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 600;
    padding: 7px 16px;
    border-radius: 999px;
    text-decoration: none;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .banner-dismiss {
    appearance: none;
    background: transparent;
    border: 0;
    color: var(--ios-label-tertiary);
    font-size: 1.4rem;
    cursor: pointer;
    padding: 4px 6px;
    line-height: 1;
    flex-shrink: 0;
  }
</style>
